import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Globe, Shield, Heart, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS, COMPANY_INFO } from '@/lib/index';
import { useLanguage } from '@/hooks/useLanguage';
import { BusinessCard } from '@/components/Cards';
import { BUSINESS_SERVICES, COMPANY_PHILOSOPHY } from '@/data/index';
import { Button } from '@/components/ui/button';

const Home: React.FC = () => {
  const { t, isEn } = useLanguage();

  // Animation presets from AGENTS.md / Design System
  const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { stiffness: 300, damping: 35 }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.OSAKA_SKYLINE_1}
            alt="Osaka Skyline"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-6 md:px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <span className="inline-block px-2 py-1 mb-4 md:mb-6 text-[10px] md:text-xs font-semibold tracking-widest uppercase border border-primary text-primary bg-primary/5">
              {isEn ? "OSAKA BASED PHYSICAL BUSINESS OPERATOR" : "大阪拠点実店舗運営"}
            </span>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold leading-tight mb-6 md:mb-8 text-foreground">
              {t({ jp: COMPANY_PHILOSOPHY.mainTitle.ja, en: COMPANY_PHILOSOPHY.mainTitle.en })}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed mb-4 md:mb-6 border-l-4 border-primary pl-4 md:pl-6">
              {t({ jp: COMPANY_PHILOSOPHY.description.ja, en: COMPANY_PHILOSOPHY.description.en })}
            </p>
            <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 font-medium">
              {isEn 
                ? "Osaka-based corporation operating physical businesses with cat hotel as our primary operation."
                : "大阪を拠点に実店舗運営を行う法人企業。猫ホテルを主事業としています。"}
            </p>
            <div className="bg-card/50 border border-border rounded-lg p-4 md:p-6 mb-6 md:mb-10">
              <p className="text-sm md:text-base font-medium text-foreground mb-3 leading-relaxed">
                {isEn 
                  ? "M.M.K Corporation is an Osaka-based operator of physical businesses including the cat-exclusive boarding hotel 'Cat Retreat Nekojarashi'."
                  : "株式会社M.M.Kは大阪を拠点とし、猫専用ペットホテル「猫じゃらし Cat Retreat」の実店舗運営を基盤に事業を展開しています。"}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                {isEn 
                  ? "Established as a legitimate Japanese corporation with physical operations and long-term business continuity in the Osaka region."
                  : "実店舗運営を基盤とし、日本国内での長期的な事業継続を前提に法人経営を行っています。"}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-6 md:px-8 w-full sm:w-auto">
                <Link to={isEn ? ROUTE_PATHS.EN_BUSINESS : ROUTE_PATHS.BUSINESS}>
                  {isEn ? "Our Business" : "事業内容を見る"}
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="rounded-none border-primary text-primary hover:bg-primary/5 px-6 md:px-8 w-full sm:w-auto" asChild>
                <Link to={isEn ? ROUTE_PATHS.EN_COMPANY : ROUTE_PATHS.COMPANY}>
                  {isEn ? "Company Overview" : "会社概要"}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Points Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {COMPANY_PHILOSOPHY.points.map((point, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col space-y-4"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary/10 text-primary mb-2">
                  {index === 0 && <Heart size={24} />}
                  {index === 1 && <Globe size={24} />}
                  {index === 2 && <Shield size={24} />}
                </div>
                <h3 className="text-xl font-bold">{t({ jp: point.title.ja, en: point.title.en })}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t({ jp: point.detail.ja, en: point.detail.en })}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Areas Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {isEn ? "Our Business Portfolios" : "主要事業領域"}
              </h2>
              <p className="text-muted-foreground">
                {isEn 
                  ? "We operate diverse businesses focused on animal welfare, international brand expansion, and multicultural bridge-building."
                  : "動物福祉への貢献、海外ブランドの日本進出支援、そして多文化間の架け橋となる事業を展開しています。"}
              </p>
            </div>
            <Button variant="ghost" className="group p-0 hover:bg-transparent text-primary font-semibold" asChild>
              <Link to={isEn ? ROUTE_PATHS.EN_BUSINESS : ROUTE_PATHS.BUSINESS}>
                {isEn ? "View All Services" : "全ての事業を見る"}
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BUSINESS_SERVICES.map((service, index) => {
              const icons = [Heart, Shield, Globe, Zap];
              const mappedService = {
                id: service.id,
                title: { jp: service.title.ja, en: service.title.en },
                description: { jp: service.description.ja, en: service.description.en },
                icon: icons[index % icons.length],
                link: service.link,
                linkText: service.linkText ? { jp: service.linkText.ja, en: service.linkText.en } : undefined
              };
              return (
                <motion.div
                  key={service.id}
                  {...fadeInUp}
                  transition={{ delay: index * 0.05 }}
                >
                  <BusinessCard service={mappedService} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cat Hotel Specific Brand Callout */}
      <section className="py-20 bg-primary text-primary-foreground overflow-hidden relative">
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 pointer-events-none">
          <img 
            src={IMAGES.OFFICE_BUILDING_1} 
            className="w-full h-full object-cover grayscale invert" 
            alt="Decoration"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold mb-6">
              {isEn ? "Hospitality for Feline Guests" : "猫専用の極上のホスピタリティ"}
            </h2>
            <p className="text-lg opacity-90 mb-8 leading-relaxed">
              {isEn 
                ? "'Nekojarashi' Cat Retreat offers a specialized environment focused on long-term safety and feline well-being. Our brand represents our commitment to the highest standards of animal care in Japan."
                : "「猫じゃらし Cat Retreat」は、長期滞在やシニア猫にも対応した完全猫専用の宿泊施設です。日本国内における最高水準の動物ケアと、オーナー様の安心を追求しています。"}
            </p>
            <Button 
              size="lg" 
              variant="secondary" 
              className="rounded-none px-10 py-6 text-lg font-bold hover:bg-white"
              asChild
            >
              <a href={COMPANY_INFO.catHotelUrl} target="_blank" rel="noopener noreferrer">
                Official Brand Website →
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Localization Focus / Global Capability Section */}
      <section className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <Globe className="text-primary mb-6" size={48} />
            <h2 className="text-3xl font-bold mb-6">
              {isEn ? "Osaka-Based Multicultural Business Capabilities" : "大阪拠点の多文化ビジネス能力"}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {isEn 
                ? "As an established Osaka corporation with physical business operations, we leverage trilingual cultural expertise across Japanese, Chinese, and English-speaking markets to provide practical localization execution and cross-border brand expansion support."
                : "大阪での実店舗運営を基盤とした法人として、日・中・英三言語環境における文化的専門性を活かし、実践的なローカライゼーション実行とクロスボーダーブランド展開支援を提供しています。"}
            </p>
            <Button variant="outline" className="rounded-none" asChild>
              <Link to={isEn ? ROUTE_PATHS.EN_MULTICULTURAL : ROUTE_PATHS.MULTICULTURAL}>
                {isEn ? "Our Multicultural Approach" : "多文化アプローチについて"}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};



export default Home;